import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowRight, Upload, FileText, Trash2, Search, Link as LinkIcon, Edit } from 'lucide-react';
import { 
  scanPurchaseInvoice, 
  uploadPurchaseInvoice, 
  getPurchaseInvoices, 
  deletePurchaseInvoice,
  getPurchaseInvoiceDownloadUrl,
  checkDuplicateInvoice
} from '../api';
import { toast } from '../hooks/use-toast';
import { useVatRate } from '../lib/vat';

const AmountFields = ({ data, setData, vatRate, prefix }) => {
  const calcVat = (total) => (total ? (parseFloat(total) * vatRate / (100 + vatRate)).toFixed(2) : '');
  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`${prefix}-amount-fields`}>
      <div className="space-y-1">
        <Label className="text-base font-semibold">סכום כולל מע"מ</Label>
        <Input type="number" value={data.totalAmount || ''} onChange={(e) => setData({ ...data, totalAmount: e.target.value, vatAmount: calcVat(e.target.value) })} className="text-lg" data-testid={`${prefix}-total-amount-input`} />
      </div>
      <div className="space-y-1">
        <Label className="text-base font-semibold">מע"מ ({vatRate}%)</Label>
        <Input type="number" value={data.vatAmount || ''} onChange={(e) => setData({ ...data, vatAmount: e.target.value })} className="text-lg" data-testid={`${prefix}-vat-amount-input`} />
      </div>
      <p className="col-span-2 text-xs text-gray-500">המע"מ מחושב אוטומטית מהסכום, ואפשר לתקן אותו ידנית. הסכומים נכנסים לדוח המע"מ לפי תאריך החשבונית.</p>
    </div>
  );
};

const PurchaseInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const vatRate = useVatRate();
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [scannedData, setScannedData] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    confidence: 0
  });
  
  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editData, setEditData] = useState({
    invoiceNumber: '',
    invoiceDate: ''
  });

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון חשבוניות',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate PDF
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: 'שגיאה',
        description: 'ניתן להעלות קבצי PDF בלבד',
        variant: 'destructive'
      });
      return;
    }

    setSelectedFile(file);
    
    // Automatically scan the PDF
    await handleScanInvoice(file);
  };

  const handleScanInvoice = async (file) => {
    setScanning(true);
    try {
      toast({
        title: 'סורק חשבונית...',
        description: 'מחלץ מידע באמצעות AI OCR'
      });

      const result = await scanPurchaseInvoice(file);
      
      setScannedData(prev => ({
        ...prev,
        invoiceNumber: result.invoiceNumber || '',
        invoiceDate: result.invoiceDate || '',
        confidence: result.confidence
      }));

      if (result.confidence > 0.7) {
        toast({
          title: 'סריקה הצליחה!',
          description: `זוהו: מספר ${result.invoiceNumber || 'לא זוהה'}, תאריך ${result.invoiceDate || 'לא זוהה'}`
        });
      } else {
        toast({
          title: 'סריקה חלקית',
          description: 'לא כל השדות זוהו. אנא הזן ידנית.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error scanning invoice:', error);
      toast({
        title: 'שגיאת סריקה',
        description: 'לא ניתן לסרוק את החשבונית. אנא הזן את הפרטים ידנית.',
        variant: 'destructive'
      });
    } finally {
      setScanning(false);
    }
  };

  const checkForDuplicate = async () => {
    if (!scannedData.invoiceNumber) {
      return false;
    }

    try {
      const result = await checkDuplicateInvoice(scannedData.invoiceNumber);
      if (result.exists) {
        setDuplicateWarning(result.invoice);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking duplicate:', error);
      return false;
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !scannedData.invoiceNumber || !scannedData.invoiceDate) {
      toast({
        title: 'שגיאה',
        description: 'יש למלא מספר חשבונית ותאריך',
        variant: 'destructive'
      });
      return;
    }

    // Check for duplicate
    const isDuplicate = await checkForDuplicate();
    if (isDuplicate) {
      return; // Show warning dialog instead
    }

    await proceedWithUpload();
  };

  const proceedWithUpload = async () => {
    try {
      setLoading(true);
      await uploadPurchaseInvoice(
        scannedData.invoiceNumber,
        scannedData.invoiceDate,
        selectedFile,
        scannedData.totalAmount || '',
        scannedData.vatAmount || ''
      );

      toast({
        title: 'הצלחה',
        description: 'החשבונית נשמרה בהצלחה'
      });

      // Reset form
      setSelectedFile(null);
      setScannedData({ invoiceNumber: '', invoiceDate: '', confidence: 0 });
      setDuplicateWarning(null);
      setIsUploadDialogOpen(false);
      
      // Reload invoices
      await loadInvoices();
    } catch (error) {
      console.error('Error uploading invoice:', error);
      const errorMsg = error?.response?.status === 409
        ? error.response.data.detail
        : 'לא ניתן לשמור חשבונית';
      toast({
        title: 'שגיאה',
        description: errorMsg,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateInput = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 8 digits (DDMMYYYY)
    const limitedDigits = digitsOnly.slice(0, 8);
    
    // Apply formatting based on length
    if (limitedDigits.length <= 2) {
      return limitedDigits;
    } else if (limitedDigits.length <= 4) {
      return `${limitedDigits.slice(0, 2)}/${limitedDigits.slice(2)}`;
    } else {
      return `${limitedDigits.slice(0, 2)}/${limitedDigits.slice(2, 4)}/${limitedDigits.slice(4)}`;
    }
  };

  const handleDateChange = (e) => {
    const inputValue = e.target.value;
    const currentFormatted = scannedData.invoiceDate;
    
    // Allow backspace/delete to work properly
    if (inputValue.length < currentFormatted.length) {
      // User is deleting - format the remaining value
      const formattedDate = formatDateInput(inputValue);
      setScannedData({ ...scannedData, invoiceDate: formattedDate });
    } else {
      // User is typing - format the new value
      const formattedDate = formatDateInput(inputValue);
      setScannedData({ ...scannedData, invoiceDate: formattedDate });
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק חשבונית זו?')) {
      return;
    }

    try {
      await deletePurchaseInvoice(invoiceId);
      toast({
        title: 'הצלחה',
        description: 'החשבונית נמחקה בהצלחה'
      });
      await loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן למחוק חשבונית',
        variant: 'destructive'
      });
    }
  };

  const handleView = (invoiceId) => {
    const url = getPurchaseInvoiceDownloadUrl(invoiceId);
    const width = 900;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      url,
      'InvoiceViewer',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`
    );
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setEditData({
      invoiceNumber: invoice.invoiceNumber || '',
      invoiceDate: invoice.invoiceDate || '',
      totalAmount: invoice.totalAmount || '',
      vatAmount: invoice.vatAmount || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editData.invoiceNumber.trim() || !editData.invoiceDate.trim()) {
      toast({
        title: 'שגיאה',
        description: 'יש למלא מספר חשבונית ותאריך',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Create FormData
      const formData = new FormData();
      formData.append('invoiceNumber', editData.invoiceNumber);
      formData.append('invoiceDate', editData.invoiceDate);
      formData.append('totalAmount', editData.totalAmount || '');
      formData.append('vatAmount', editData.vatAmount || '');
      
      // Update invoice
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-invoices/${editingInvoice.id}`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to update invoice');
      }

      toast({
        title: 'הצלחה',
        description: 'החשבונית עודכנה בהצלחה'
      });

      // Close dialog and reload
      setIsEditDialogOpen(false);
      setEditingInvoice(null);
      await loadInvoices();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לעדכן חשבונית',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter invoices based on search term
  const filteredInvoices = invoices.filter(invoice => 
    invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.invoiceDate.includes(searchTerm) ||
    invoice.fileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-purple-700">
              עריכת פרטי חשבונית
            </DialogTitle>
          </DialogHeader>
          
          {editingInvoice && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded border-r-4 border-blue-500">
                <p className="text-sm text-blue-800">
                  <strong>קובץ:</strong> {editingInvoice.fileName}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-invoiceNumber" className="text-base font-semibold">
                  מספר חשבונית <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-invoiceNumber"
                  value={editData.invoiceNumber}
                  onChange={(e) => setEditData({ ...editData, invoiceNumber: e.target.value })}
                  placeholder="הזן מספר חשבונית"
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-invoiceDate" className="text-base font-semibold">
                  תאריך חשבונית <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-invoiceDate"
                  value={editData.invoiceDate}
                  onChange={(e) => {
                    const formatted = formatDateInput(e.target.value);
                    setEditData({ ...editData, invoiceDate: formatted });
                  }}
                  placeholder="יום/חודש/שנה (DD/MM/YYYY)"
                  className="text-lg"
                  maxLength={10}
                />
                <p className="text-xs text-gray-500">פורמט: DD/MM/YYYY (למשל: 25/11/2024)</p>
              </div>
              <AmountFields data={editData} setData={setEditData} vatRate={vatRate} prefix="edit" />

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'שומר...' : 'שמור'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                  disabled={loading}
                  className="flex-1"
                >
                  ביטול
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <Card className="shadow-2xl border-t-4 border-purple-500">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl font-bold">חשבוניות קנייה</CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="p-8">
            {/* Scan Button and Search */}
            <div className="flex justify-between items-center mb-6 gap-4">
              <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-lg px-8 py-6"
                    onClick={() => {
                      setSelectedFile(null);
                      setScannedData({ invoiceNumber: '', invoiceDate: '', confidence: 0 });
                    }}
                  >
                    <Upload className="ml-2 w-6 h-6" />
                    סריקת חשבונית חדשה
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl" dir="rtl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">סריקת חשבונית באמצעות AI</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-6 mt-4">
                    {/* File Upload */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                      <input
                        type="file"
                        id="invoice-file"
                        accept=".pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={scanning}
                      />
                      <label
                        htmlFor="invoice-file"
                        className={`flex flex-col items-center gap-3 cursor-pointer ${
                          scanning ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Upload className="w-16 h-16 text-purple-500" />
                        <div className="text-lg font-semibold text-gray-700">
                          {scanning ? 'סורק...' : selectedFile ? selectedFile.name : 'לחץ להעלאת קובץ PDF'}
                        </div>
                        <div className="text-sm text-gray-500">
                          המערכת תסרוק אוטומטית ותחלץ מספר חשבונית ותאריך
                        </div>
                      </label>
                    </div>

                    {/* Scanned Data Display */}
                    {selectedFile && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-blue-900">תוצאות הסריקה</span>
                            {scannedData.confidence > 0 && (
                              <span className={`text-sm px-2 py-1 rounded ${
                                scannedData.confidence > 0.7 ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                              }`}>
                                דיוק: {(scannedData.confidence * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Duplicate Warning */}
                        {duplicateWarning && (
                          <div className="bg-yellow-50 border-r-4 border-yellow-500 p-4 rounded mb-4">
                            <div className="flex items-start gap-3">
                              <div className="text-yellow-600 text-2xl">⚠️</div>
                              <div className="flex-1">
                                <h4 className="font-bold text-yellow-900 mb-2">
                                  אזהרה: מספר חשבונית כבר קיים!
                                </h4>
                                <p className="text-yellow-800 mb-3">
                                  מספר חשבונית <strong>{duplicateWarning.invoiceNumber}</strong> כבר קיים במערכת.
                                </p>
                                <div className="text-sm text-yellow-700 mb-3">
                                  <div>תאריך: {duplicateWarning.invoiceDate}</div>
                                  <div>קובץ: {duplicateWarning.fileName}</div>
                                </div>
                                <div className="flex gap-2 mt-4">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setDuplicateWarning(null);
                                      setScannedData({ ...scannedData, invoiceNumber: '' });
                                    }}
                                    className="bg-white"
                                  >
                                    שנה מספר חשבונית
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      setDuplicateWarning(null);
                                      await proceedWithUpload();
                                    }}
                                    className="bg-yellow-600 hover:bg-yellow-700"
                                  >
                                    המשך בכל זאת
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="invoiceNumber" className="text-base font-semibold">
                              מספר חשבונית <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="invoiceNumber"
                              value={scannedData.invoiceNumber}
                              onChange={(e) => {
                                setScannedData({ ...scannedData, invoiceNumber: e.target.value });
                                setDuplicateWarning(null); // Clear warning when user changes number
                              }}
                              placeholder="הזן מספר חשבונית"
                              className="text-lg"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="invoiceDate" className="text-base font-semibold">
                              תאריך חשבונית <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="invoiceDate"
                              value={scannedData.invoiceDate}
                              onChange={handleDateChange}
                              placeholder="DD/MM/YYYY"
                              className="text-lg"
                              maxLength={10}
                            />
                          </div>
                        </div>
                        <div className="mt-4"><AmountFields data={scannedData} setData={setScannedData} vatRate={vatRate} prefix="upload" /></div>

                        <div className="flex justify-end gap-3 mt-6">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setIsUploadDialogOpen(false);
                              setSelectedFile(null);
                              setScannedData({ invoiceNumber: '', invoiceDate: '', confidence: 0 });
                            }}
                          >
                            ביטול
                          </Button>
                          <Button
                            onClick={handleUpload}
                            disabled={loading || !scannedData.invoiceNumber || !scannedData.invoiceDate}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {loading ? 'שומר...' : 'שמור חשבונית'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="חפש לפי מספר חשבונית, תאריך או שם קובץ..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pr-10 text-lg"
                  />
                </div>
              </div>
            </div>

            {/* Invoices Table */}
            {loading && invoices.length === 0 ? (
              <div className="text-center py-12 text-gray-500">טוען חשבוניות...</div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  {searchTerm ? 'לא נמצאו חשבוניות תואמות' : 'אין חשבוניות עדיין. התחל בסריקת חשבונית ראשונה!'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
                <table className="w-full border-collapse bg-white">
                  <thead>
                    <tr className="bg-gradient-to-r from-purple-500 to-blue-500 text-white">
                      <th className="border border-gray-300 p-4 text-center font-semibold">מספר חשבונית</th>
                      <th className="border border-gray-300 p-4 text-center font-semibold">תאריך חשבונית</th>
                      <th className="border border-gray-300 p-4 text-center font-semibold">סכום כולל מע"מ</th>
                      <th className="border border-gray-300 p-4 text-center font-semibold">מע"מ</th>
                      <th className="border border-gray-300 p-4 text-center font-semibold">שם קובץ</th>
                      <th className="border border-gray-300 p-4 text-center font-semibold">תאריך העלאה</th>
                      <th className="border border-gray-300 p-4 text-center font-semibold w-40">פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-purple-50 transition-colors">
                        <td className="border border-gray-300 p-4 text-center font-bold text-purple-700">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="border border-gray-300 p-4 text-center">
                          {invoice.invoiceDate}
                        </td>
                        <td className="border border-gray-300 p-4 text-center font-semibold" data-testid={`purchase-total-${invoice.id}`}>
                          {invoice.totalAmount ? `₪${parseFloat(invoice.totalAmount).toFixed(2)}` : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="border border-gray-300 p-4 text-center text-orange-700" data-testid={`purchase-vat-${invoice.id}`}>
                          {invoice.vatAmount ? `₪${parseFloat(invoice.vatAmount).toFixed(2)}` : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="border border-gray-300 p-4 text-center text-sm">
                          {invoice.fileName}
                        </td>
                        <td className="border border-gray-300 p-4 text-center text-sm text-gray-600">
                          {new Date(invoice.uploadDate).toLocaleDateString('he-IL')}
                        </td>
                        <td className="border border-gray-300 p-4">
                          <div className="flex gap-2 justify-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleView(invoice.id)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="צפה בחשבונית"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(invoice)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="ערוך פרטים"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(invoice.id)}
                              className="hover:bg-red-700"
                              title="מחק חשבונית"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary */}
            {filteredInvoices.length > 0 && (
              <div className="mt-6 text-center text-gray-600">
                <span className="font-semibold">{filteredInvoices.length}</span> חשבוניות
                {searchTerm && ` (מתוך ${invoices.length})`}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default PurchaseInvoices;
